import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx } from "vue";
const _hoisted_1 = {
    class: "px-5 pt-6 pb-4 bg-white",
    "data-testid": "profile-view"
};
const _hoisted_2 = { class: "max-w-270 mx-auto" };
const _hoisted_3 = { class: "flex items-center justify-between mb-6 sm:mb-8" };
const _hoisted_4 = {
    "data-testid": "profile-title",
    class: "text-2xl sm:text-3xl font-bold text-gray-900 shrink-0 mr-60"
};
const _hoisted_5 = {
    key: 0,
    class: "flex items-center gap-3"
};
const _hoisted_6 = { class: "shrink-0" };
const _hoisted_7 = {
    key: 0,
    "data-testid": "profile-loading",
    class: "flex flex-col items-center justify-center py-24"
};
const _hoisted_8 = { class: "text-sm text-gray-500" };
const _hoisted_9 = {
    key: 1,
    "data-testid": "profile-error",
    class: "flex flex-col items-center justify-center py-24"
};
const _hoisted_10 = { class: "text-sm text-gray-700 mb-4" };
const _hoisted_11 = { class: "text-sm text-black mt-4" };
const _hoisted_12 = ["id", "aria-labelledby"];
const _hoisted_13 = { class: "mb-8" };
const _hoisted_14 = { class: "grid grid-cols-1 md:grid-cols-2 gap-6" };
const _hoisted_15 = ["id", "aria-labelledby"];
const _hoisted_16 = {
    key: 0,
    class: "mb-8"
};
const _hoisted_17 = { class: "text-base sm:text-lg font-semibold text-gray-900 mb-4" };
const _hoisted_18 = { class: "grid grid-cols-1 md:grid-cols-2 gap-6" };
const _hoisted_19 = { key: 1 };
const _hoisted_20 = { class: "text-base sm:text-lg font-semibold text-gray-900 mb-4" };
const _hoisted_21 = { class: "grid grid-cols-1 md:grid-cols-2 gap-6" };
const _hoisted_22 = { class: "md:col-span-2" };
import { ButtonGroup, buttonGroupTabId, Button, ImageUploadInput, FormErrorSummary, FormSaveButton, DynamicFormField, UnsavedChangesModal, useToast, useFormValidation, useFormSubmit, createPhoneValidator, PHONE_FORMATS_BY_DIAL_CODE, significantDigitCount, } from "@eudonet/opal";
import { reactive, ref, computed, watch, onMounted, nextTick, inject, useId } from "vue";
import { useI18n } from "vue-i18n";
import { buildDateFormatOptions } from "../../infrastructure/utils/DateFormatHelper";
import { translateProfileFields } from "../../infrastructure/utils/profileFieldTranslations";
import { localizeTimeZoneSelectOptions } from "../../infrastructure/utils/timeZoneLocale";
import { getFieldConfiguration, getFieldConfigurations, getLanguageTimeConfigurations, } from "../../infrastructure/api";
import { ProfileService } from "../../infrastructure/adapters/ProfileService";
import { useProfileStore } from "../../stores/useProfileStore";
import { useDirtyState } from "@/shared/composables/forms/useDirtyState";
import { SHELL_CONTRACTS_KEY } from "@/contracts/ShellContracts";
import { ShellEvent } from "@/contracts/types";
import { setLocale } from "@/plugins/i18n";
import { resolveApiErrorKey } from "@/infrastructure/services/ApiErrorService";
// errorCode → i18n key (R70, OP-7457): the BFF/OPUS B2C validator refuses a blocked-TLD address
// (user@gmail.test) with this code — without the mapping the user got the generic save-error
// toast.
//
// OP-7542 deliberately did NOT put an `isValidEmail` overlay on this field the way `phoneValidator`
// overlays the phone one: the email field is `"readonly": true` in the BFF schema
// (`EUDO.OnePlatformBFF/Schemas/myProfileFieldSchema.json`, `type: "email"` entry), which
// `DynamicFormField` binds straight onto the OPAL `EmailInput` as `:readonly`. The user cannot type
// here, so an inline "enter a valid email" under an uneditable field would be unreachable when the
// stored value is fine and useless when it is not. The code is still reachable — `ProfileService`
// posts `email` on every save — but only when the value seeded from Keycloak/OPUS is already
// blocked, which the user has no way to correct from this form. A toast is the correct channel for
// that; make this a field error only if the schema ever makes the field editable.
//
// `UserProfile.Email.Required` stays on the generic fallback: the email field is required
// client-side, so it is unreachable from this form (internal-only per R70).
export default /*@__PURE__*/ _defineComponent({
    __name: 'ProfileManagement',
    setup(__props) {
        const PROFILE_SAVE_ERROR_KEYS = {
            "UserProfile.Email.Invalid": "profile.validation.emailInvalid",
        };
        const { t, te, locale } = useI18n();
        const { showToast } = useToast();
        const contracts = inject(SHELL_CONTRACTS_KEY);
        // Tabs (translated)
        const tabs = computed(() => [
            { key: "personal_info", label: t("tabs.personalInfo") },
            { key: "language_time", label: t("tabs.languageTime") },
        ]);
        const activeTab = ref("personal_info");
        const panelId = useId();
        // Field configurations (presented to the form — updated when locale changes)
        const personalInfoFields = ref([]);
        const languageTimeFields = ref([]);
        /** Raw schema from BFF (single source); labels are overlaid from i18n in syncProfileFieldPresentation */
        const rawPersonalInfoFields = ref([]);
        const rawLanguageTimeBlueprint = ref([]);
        const timeZoneOptionsRaw = ref([]);
        // Lookup language/time fields by key instead of fragile index
        const langFieldsByKey = computed(() => Object.fromEntries(languageTimeFields.value.map((f) => [f.key, f])));
        const langField = (key) => langFieldsByKey.value[key];
        // Profile data (loaded from API)
        const profileData = reactive({
            fullName: "",
            profilePicture: "",
            email: "",
            firstName: "",
            surname: "",
            phoneNumber: "",
            phoneCountryCode: "",
            phoneNationalNumber: "",
            department: "",
            jobTitle: "",
            displayLanguage: "en-GB",
            timeZone: "Europe/Paris",
            timeFormat: "24",
            dateFormat: "DD/MM/YYYY",
            permissions: [],
            accessibleApplications: [],
            defaultApplicationCode: null,
        });
        // Profile service instance
        const profileService = new ProfileService();
        const profileStore = useProfileStore();
        // Pending avatar file (set when user picks a new image, cleared on save)
        const pendingAvatarFile = ref(null);
        const avatarRemoved = ref(false);
        // Snapshot of avatar state before the current modal session, so Cancel can revert
        // only what changed in this session (not previously confirmed uploads/removes).
        const preModalAvatarState = ref(null);
        // Loading & error state
        const isLoading = ref(true);
        const loadError = ref(false);
        // Modal visibility and pending tab navigation
        const showUnsavedChangesModal = ref(false);
        const pendingTabKey = ref(null);
        // Store route navigation callbacks
        const pendingRouteCallbacks = {
            proceed: null,
            cancel: null,
        };
        // Dirty state tracking — also handles original data backup & reset
        const { isDirty, markAsSaved, resetToOriginal, setDirty } = useDirtyState({
            data: profileData,
            onNavigationBlocked: (proceed, cancel) => {
                showUnsavedChangesModal.value = true;
                pendingRouteCallbacks.proceed = proceed;
                pendingRouteCallbacks.cancel = cancel;
            },
            enableBeforeUnload: true,
        });
        // Form validation with auto-clear on user correction
        const { errorList, hasErrors, validateAll, validateAndSetField, getFieldError, clearAllErrors } = useFormValidation({
            requiredMessage: () => t("profile.validation.required"),
            watchData: profileData,
        });
        // Raw digits as typed (trunk zero included) — createPhoneValidator applies its own single
        // significantDigitCount strip, so feeding it the already-stripped profileData.phoneNationalNumber
        // would strip a second zero and silently accept an over-typed number. Falls back to
        // phoneNationalNumber before any country-change event has fired (the loaded value, already bare
        // significant digits, so stripping it again is a no-op).
        const rawPhoneNationalNumber = ref("");
        // Phone validator (uses shared validator factory)
        const phoneValidator = createPhoneValidator(() => rawPhoneNationalNumber.value || profileData.phoneNationalNumber || "", () => profileData.phoneCountryCode || "", (params) => t("profile.validation.phoneDigits", params));
        // All fields combined for validation (with phone validator injected)
        const allFields = computed(() => [
            ...personalInfoFields.value.map((f) => f.type === "phone" ? { ...f, validate: phoneValidator } : f),
            ...languageTimeFields.value,
        ]);
        // Total number of fields in the form (drives error alert display mode)
        const totalFieldCount = computed(() => allFields.value.length);
        // Save is allowed only when the form is dirty, has no errors, and all required fields are filled
        const canSave = computed(() => {
            if (!isDirty.value)
                return false;
            if (hasErrors.value)
                return false;
            return allFields.value
                .filter((f) => f.required)
                .every((f) => {
                const value = profileData[f.key];
                return value !== undefined && value !== null && String(value).trim() !== "";
            });
        });
        // Validate a single field on blur so errors appear immediately when leaving the field
        const handleFieldBlur = (fieldKey) => {
            const field = allFields.value.find((f) => f.key === fieldKey);
            if (!field)
                return;
            validateAndSetField(field, profileData[field.key]);
        };
        // Determine which tab a field belongs to
        const getFieldTab = (key) => {
            if (personalInfoFields.value.some((f) => f.key === key))
                return "personal_info";
            return "language_time";
        };
        // Scroll to a specific field (switching tab if needed)
        const scrollToField = async (key) => {
            const tab = getFieldTab(key);
            if (tab !== activeTab.value) {
                activeTab.value = tab;
                await nextTick();
            }
            const el = document.getElementById(`field-${key}`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                const input = el.querySelector("input, select");
                if (input instanceof HTMLElement)
                    input.focus();
            }
        };
        // Submit flow via composable (validate → save → toast)
        const { isSaving, handleSubmit } = useFormSubmit({
            validate: () => validateAll(allFields.value, profileData),
            save: async () => {
                await profileService.updateProfile(profileData, pendingAvatarFile.value, avatarRemoved.value);
                pendingAvatarFile.value = null;
                avatarRemoved.value = false;
                preModalAvatarState.value = null;
            },
            onSuccess: async () => {
                clearAllErrors();
                // Mark as saved immediately so the button stays disabled during the reload
                markAsSaved();
                // Reload profile to get the real avatar URL from the server
                profileStore.clearProfile();
                const profile = await profileStore.fetchProfile();
                Object.assign(profileData, profile);
                await nextTick();
                markAsSaved();
                if (profile.displayLanguage) {
                    setLocale(profile.displayLanguage);
                }
                contracts?.eventBus.emit(ShellEvent.USER_PROFILE_UPDATED, profile);
                showToast({
                    message: t("form.saveSuccess"),
                    ariaClose: t("form.dismissToast"),
                    status: "success",
                    duration: 5000,
                });
            },
            onError: (error) => {
                showToast({
                    message: t(resolveApiErrorKey(error, PROFILE_SAVE_ERROR_KEYS, "form.saveError")),
                    ariaClose: t("form.dismissToast"),
                    status: "error",
                });
            },
        });
        // Handle tab change with dirty state check
        const handleTabChange = (tabKey) => {
            if (tabKey === activeTab.value)
                return;
            if (isDirty.value) {
                pendingTabKey.value = tabKey;
                showUnsavedChangesModal.value = true;
            }
            else {
                activeTab.value = tabKey;
            }
        };
        // Handle "Stay" button click in modal
        const handleStay = () => {
            showUnsavedChangesModal.value = false;
            pendingTabKey.value = null;
            if (pendingRouteCallbacks.cancel) {
                pendingRouteCallbacks.cancel();
                pendingRouteCallbacks.proceed = null;
                pendingRouteCallbacks.cancel = null;
            }
        };
        // Handle "Leave" button click in modal
        const handleLeave = () => {
            showUnsavedChangesModal.value = false;
            // Reset avatar pending state
            pendingAvatarFile.value = null;
            avatarRemoved.value = false;
            preModalAvatarState.value = null;
            // Reset profile data to original state via useDirtyState
            resetToOriginal();
            if (pendingTabKey.value) {
                activeTab.value = pendingTabKey.value;
                pendingTabKey.value = null;
                markAsSaved();
                clearAllErrors();
            }
            if (pendingRouteCallbacks.proceed) {
                pendingRouteCallbacks.proceed();
                pendingRouteCallbacks.proceed = null;
                pendingRouteCallbacks.cancel = null;
            }
        };
        function syncProfileFieldPresentation() {
            if (!rawPersonalInfoFields.value.length && !rawLanguageTimeBlueprint.value.length) {
                return;
            }
            personalInfoFields.value = translateProfileFields(rawPersonalInfoFields.value, t);
            const loc = locale.value;
            const languageFields = rawLanguageTimeBlueprint.value.map((field) => {
                if (field.key === "timeZone") {
                    return {
                        ...field,
                        options: localizeTimeZoneSelectOptions(timeZoneOptionsRaw.value, loc),
                    };
                }
                if (field.key === "dateFormat") {
                    return { ...field, options: buildDateFormatOptions(loc) };
                }
                return { ...field };
            });
            languageTimeFields.value = translateProfileFields(languageFields, t, te);
        }
        // Load data from API
        const loadProfileData = async () => {
            isLoading.value = true;
            loadError.value = false;
            try {
                const [fieldConfig, timeZoneOptions] = await Promise.all([
                    getFieldConfiguration(),
                    profileService.getAvailableTimeZones(),
                ]);
                rawPersonalInfoFields.value = getFieldConfigurations(fieldConfig);
                rawLanguageTimeBlueprint.value = getLanguageTimeConfigurations(fieldConfig);
                timeZoneOptionsRaw.value = timeZoneOptions;
                syncProfileFieldPresentation();
                const profile = await profileStore.fetchProfile();
                Object.assign(profileData, profile);
                await nextTick();
                markAsSaved();
            }
            catch {
                loadError.value = true;
            }
            finally {
                isLoading.value = false;
            }
        };
        onMounted(loadProfileData);
        watch(locale, () => {
            syncProfileFieldPresentation();
            clearAllErrors();
        });
        const captureAvatarStateIfNeeded = () => {
            if (!preModalAvatarState.value) {
                preModalAvatarState.value = {
                    profilePicture: profileData.profilePicture,
                    pendingFile: pendingAvatarFile.value,
                    removed: avatarRemoved.value,
                };
            }
        };
        const handleImageUpload = (file) => {
            captureAvatarStateIfNeeded();
            pendingAvatarFile.value = file;
            avatarRemoved.value = false;
            profileData.profilePicture = URL.createObjectURL(file);
            // Upload confirms the change — discard the snapshot
            preModalAvatarState.value = null;
            setDirty(true);
        };
        const handleImageRemove = () => {
            captureAvatarStateIfNeeded();
            pendingAvatarFile.value = null;
            avatarRemoved.value = true;
            profileData.profilePicture = "";
            setDirty(true);
        };
        const handleImageCancel = () => {
            if (preModalAvatarState.value) {
                profileData.profilePicture = preModalAvatarState.value.profilePicture;
                pendingAvatarFile.value = preModalAvatarState.value.pendingFile;
                avatarRemoved.value = preModalAvatarState.value.removed;
                preModalAvatarState.value = null;
                // Only clear manual dirty if no avatar change remains pending.
                // Note: setDirty(false) only clears the manual flag; if other form
                // fields were edited, isDirty stays true via deepEqual in useDirtyState.
                if (!pendingAvatarFile.value && !avatarRemoved.value) {
                    setDirty(false);
                }
            }
        };
        // PhoneInput/DynamicFormField's country-change carries the raw typed national number, trunk zero
        // included (OP-7665). OPUS stores country code and national number as separate fields rather than
        // a single E.164 string, so toStoredPhoneValue doesn't fit here — strip the trunk zero the same
        // way it does internally, via significantDigitCount, before persisting.
        const handlePhoneCountryChange = (payload) => {
            profileData.phoneCountryCode = payload.dialCode;
            rawPhoneNationalNumber.value = payload.nationalNumber;
            const hasTrunkZero = PHONE_FORMATS_BY_DIAL_CODE[payload.dialCode]?.hasTrunkZero ?? false;
            profileData.phoneNationalNumber = significantDigitCount(payload.nationalNumber, hasTrunkZero);
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("h1", _hoisted_4, _toDisplayString(_ctx.$t("profile.title")), 1 /* TEXT */),
                        (!isLoading.value && !loadError.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                (totalFieldCount.value <= 10)
                                    ? (_openBlock(), _createBlock(_unref(FormErrorSummary), {
                                        key: 0,
                                        mode: "inline",
                                        errors: _unref(errorList),
                                        heading: _ctx.$t('profile.validation.alertHeading')
                                    }, null, 8 /* PROPS */, ["errors", "heading"]))
                                    : (_openBlock(), _createBlock(_unref(FormErrorSummary), {
                                        key: 1,
                                        mode: "dropdown",
                                        errors: _unref(errorList),
                                        "trigger-label": _ctx.$t('profile.validation.errorsCount', _unref(errorList).length),
                                        "list-heading": _ctx.$t('profile.validation.errorFieldsTitle'),
                                        onNavigateToField: scrollToField
                                    }, null, 8 /* PROPS */, ["errors", "trigger-label", "list-heading"])),
                                _createElementVNode("div", _hoisted_6, [
                                    _createVNode(_unref(FormSaveButton), {
                                        enabled: canSave.value,
                                        saving: _unref(isSaving),
                                        "save-label": _ctx.$t('form.saveChanges'),
                                        "saving-label": _ctx.$t('form.saving'),
                                        "disabled-hint": _ctx.$t('form.saveDisabledHint'),
                                        onSave: _unref(handleSubmit)
                                    }, null, 8 /* PROPS */, ["enabled", "saving", "save-label", "saving-label", "disabled-hint", "onSave"])
                                ])
                            ]))
                            : _createCommentVNode("v-if", true)
                    ]),
                    (isLoading.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                            _cache[6] || (_cache[6] = _createElementVNode("i", { class: "ri-loader-4-line animate-spin text-4xl text-gray-400 mb-4" }, null, -1 /* CACHED */)),
                            _createElementVNode("p", _hoisted_8, _toDisplayString(_ctx.$t("profile.loading")), 1 /* TEXT */)
                        ]))
                        : (loadError.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                _cache[7] || (_cache[7] = _createElementVNode("i", { class: "ri-error-warning-line text-4xl text-red-400 mb-4" }, null, -1 /* CACHED */)),
                                _createElementVNode("p", _hoisted_10, _toDisplayString(_ctx.$t("profile.loadError")), 1 /* TEXT */),
                                _createVNode(_unref(Button), {
                                    variant: "secondary",
                                    "data-testid": "profile-retry",
                                    label: _ctx.$t('profile.retry'),
                                    onClick: loadProfileData
                                }, null, 8 /* PROPS */, ["label"])
                            ]))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                _createElementVNode("div", null, [
                                    _createVNode(_unref(ButtonGroup), {
                                        "model-value": activeTab.value,
                                        "panel-id": _unref(panelId),
                                        "overflow-label": _ctx.$t('common.tabs.next'),
                                        "overflow-previous-label": _ctx.$t('common.tabs.previous'),
                                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => (handleTabChange($event)))
                                    }, {
                                        default: _withCtx(() => [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabs.value, (btn) => {
                                                return (_openBlock(), _createBlock(_unref(Button), {
                                                    key: btn.key,
                                                    variant: "tertiary",
                                                    "data-testid": `profile-tab-${btn.key}`,
                                                    label: btn.label,
                                                    value: btn.key
                                                }, null, 8 /* PROPS */, ["data-testid", "label", "value"]));
                                            }), 128 /* KEYED_FRAGMENT */))
                                        ]),
                                        _: 1 /* STABLE */
                                    }, 8 /* PROPS */, ["model-value", "panel-id", "overflow-label", "overflow-previous-label"])
                                ]),
                                _createElementVNode("p", _hoisted_11, _toDisplayString(_ctx.$t("profile.requiredFieldsNote")), 1 /* TEXT */),
                                (activeTab.value === 'personal_info')
                                    ? (_openBlock(), _createElementBlock("div", {
                                        key: 0,
                                        id: _unref(panelId),
                                        role: "tabpanel",
                                        "aria-labelledby": _unref(buttonGroupTabId)(_unref(panelId), activeTab.value),
                                        class: "mt-4 sm:mt-8"
                                    }, [
                                        _createElementVNode("div", _hoisted_13, [
                                            _createVNode(_unref(ImageUploadInput), {
                                                "image-url": profileData.profilePicture,
                                                "image-alt": _ctx.$t('profile.avatar.imageAlt'),
                                                label: _ctx.$t('profile.avatar.label'),
                                                "edit-button-label": _ctx.$t('profile.avatar.editButton'),
                                                "modal-title": _ctx.$t('profile.avatar.modalTitle'),
                                                "cancel-button-label": _ctx.$t('profile.avatar.cancelButton'),
                                                "upload-button-label": _ctx.$t('profile.avatar.uploadButton'),
                                                "remove-button-label": _ctx.$t('profile.avatar.removeButton'),
                                                "browse-button-text": _ctx.$t('profile.avatar.browseButton'),
                                                placeholder: _ctx.$t('profile.avatar.placeholder'),
                                                "max-file-size-text": _ctx.$t('profile.avatar.maxFileSizeText'),
                                                "file-type-error-text": _ctx.$t('profile.avatar.fileTypeError'),
                                                "file-size-error-text": _ctx.$t('profile.avatar.fileSizeError', {
                                                    maxFileSize: _ctx.$t('profile.avatar.maxFileSizeText'),
                                                }),
                                                accept: "image/png,image/jpeg",
                                                "max-file-size": 10 * 1024 * 1024,
                                                "show-alert": false,
                                                onUpload: handleImageUpload,
                                                onRemove: handleImageRemove,
                                                onCancel: handleImageCancel
                                            }, null, 8 /* PROPS */, ["image-url", "image-alt", "label", "edit-button-label", "modal-title", "cancel-button-label", "upload-button-label", "remove-button-label", "browse-button-text", "placeholder", "max-file-size-text", "file-type-error-text", "file-size-error-text"])
                                        ]),
                                        _createElementVNode("div", _hoisted_14, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(personalInfoFields.value, (field) => {
                                                return (_openBlock(), _createBlock(_unref(DynamicFormField), {
                                                    key: field.key,
                                                    modelValue: profileData[field.key],
                                                    "onUpdate:modelValue": ($event) => ((profileData[field.key]) = $event),
                                                    field: field,
                                                    error: _unref(getFieldError)(field.key),
                                                    onBlur: ($event) => (handleFieldBlur(field.key)),
                                                    onCountryChange: handlePhoneCountryChange
                                                }, null, 8 /* PROPS */, ["modelValue", "onUpdate:modelValue", "field", "error", "onBlur"]));
                                            }), 128 /* KEYED_FRAGMENT */))
                                        ])
                                    ], 8 /* PROPS */, _hoisted_12))
                                    : (activeTab.value === 'language_time')
                                        ? (_openBlock(), _createElementBlock("div", {
                                            key: 1,
                                            id: _unref(panelId),
                                            role: "tabpanel",
                                            "aria-labelledby": _unref(buttonGroupTabId)(_unref(panelId), activeTab.value),
                                            class: "mt-4 sm:mt-8"
                                        }, [
                                            (langField('displayLanguage'))
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                                    _createElementVNode("h2", _hoisted_17, _toDisplayString(_ctx.$t("profile.language.title")), 1 /* TEXT */),
                                                    _createElementVNode("div", _hoisted_18, [
                                                        _createVNode(_unref(DynamicFormField), {
                                                            modelValue: profileData.displayLanguage,
                                                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((profileData.displayLanguage) = $event)),
                                                            field: langField('displayLanguage'),
                                                            error: _unref(getFieldError)('displayLanguage')
                                                        }, null, 8 /* PROPS */, ["modelValue", "field", "error"])
                                                    ])
                                                ]))
                                                : _createCommentVNode("v-if", true),
                                            (langField('timeZone'))
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                                    _createElementVNode("h2", _hoisted_20, _toDisplayString(_ctx.$t("profile.timeAndDate.title")), 1 /* TEXT */),
                                                    _createElementVNode("div", _hoisted_21, [
                                                        _createElementVNode("div", _hoisted_22, [
                                                            _createVNode(_unref(DynamicFormField), {
                                                                modelValue: profileData.timeZone,
                                                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((profileData.timeZone) = $event)),
                                                                field: langField('timeZone'),
                                                                error: _unref(getFieldError)('timeZone')
                                                            }, null, 8 /* PROPS */, ["modelValue", "field", "error"])
                                                        ]),
                                                        (langField('timeFormat'))
                                                            ? (_openBlock(), _createBlock(_unref(DynamicFormField), {
                                                                key: 0,
                                                                modelValue: profileData.timeFormat,
                                                                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((profileData.timeFormat) = $event)),
                                                                field: langField('timeFormat'),
                                                                error: _unref(getFieldError)('timeFormat')
                                                            }, null, 8 /* PROPS */, ["modelValue", "field", "error"]))
                                                            : _createCommentVNode("v-if", true),
                                                        (langField('dateFormat'))
                                                            ? (_openBlock(), _createBlock(_unref(DynamicFormField), {
                                                                key: 1,
                                                                modelValue: profileData.dateFormat,
                                                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((profileData.dateFormat) = $event)),
                                                                field: langField('dateFormat'),
                                                                error: _unref(getFieldError)('dateFormat')
                                                            }, null, 8 /* PROPS */, ["modelValue", "field", "error"]))
                                                            : _createCommentVNode("v-if", true)
                                                    ])
                                                ]))
                                                : _createCommentVNode("v-if", true)
                                        ], 8 /* PROPS */, _hoisted_15))
                                        : _createCommentVNode("v-if", true)
                            ], 64 /* STABLE_FRAGMENT */))
                ]),
                _createVNode(_unref(UnsavedChangesModal), {
                    modelValue: showUnsavedChangesModal.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((showUnsavedChangesModal).value = $event)),
                    title: _ctx.$t('unsavedChanges.title'),
                    message: _ctx.$t('unsavedChanges.message'),
                    "stay-label": _ctx.$t('unsavedChanges.stay'),
                    "leave-label": _ctx.$t('unsavedChanges.leave'),
                    onStay: handleStay,
                    onLeave: handleLeave
                }, null, 8 /* PROPS */, ["modelValue", "title", "message", "stay-label", "leave-label"])
            ]));
        };
    }
});
